// File: src/pages/roles/AddRoleMaster.jsx  (MASTER — CREATE)
import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Card, Button, Label, Input, Form } from "reactstrap";
import { useNavigate } from "react-router-dom";
import swal from "sweetalert";

/* ===== API calls ===== */
import { CreateRoleByMaster } from "../../import/ApiEndPoint/ApiCalling";

/* ===== Role matrix source ===== */
import { Roles } from "../../import/AddRole";

/* ===== Page-scoped CSS (keeps your design) ===== */
const injectOnce = () => {
  if (document.getElementById("addrole-inline-css")) return;
  const s = document.createElement("style");
  s.id = "addrole-inline-css";
  s.textContent = `
    .p-toolbar .count-pill{ background:#eef2ff; color:#3730a3; font-weight:700; padding:6px 10px; border-radius:999px; }
    .p-card{ border-radius:14px; border:1px solid #e5e7eb; }
    .grp-row{ border:1px solid #e5e7eb; border-radius:10px; overflow:hidden; }
    .grp-head{ display:flex; align-items:center; gap:10px; padding:10px 12px; background:#f4f7f6; cursor:pointer; }
    .grp-head:hover{ background:#f1f5f9; }
    .grp-title{ font-weight:700; }
    .pill{ border:1px solid #e5e7eb; padding:2px 8px; border-radius:999px; font-size:12px; background:#fff; }
    .perm-row{ display:flex; align-items:center; gap:8px; padding:8px 12px; border-top:1px solid #f1f5f9; }
    .perm-col{ flex:1; display:flex; justify-content:center; align-items:center; gap:6px; }
    .perm-name{ flex:2; text-align:left; font-weight:600; }
    .toggle{ min-width:20px; font-weight:800; user-select:none; }
    .p-card label{ font-size:12px; margin-bottom:4px; }
    .p-card input{ font-size:12px; height:34px; }
    .hdr-check{ width:16px; height:16px; }
    .disabled-box{ opacity:.45; }
  `;
  document.head.appendChild(s);
};

export default function AddRoleMaster() {
  const navigate = useNavigate();

  // fields
  const [Desc, setDesc] = useState("");
  const [Role, setRole] = useState("");
  const [Database, setDatabase] = useState("");

  // selections
  const [Selected, setSelected] = useState([]); // [{ pagename, permission: [] }]
  const [expanded, setExpanded] = useState({}); // { groupIndex: boolean }

  // MASTER pages always show all 6 columns
  const columns = [
    "View",
    "Create",
    "Edit",
    "Delete",
    "Download",
    "BulkUpload",
  ];

  useEffect(() => injectOnce(), []);

  // helpers to manage Selected
  const getPerm = (tabTitle) =>
    Selected.find((x) => x.pagename === tabTitle)?.permission || [];

  const setPerm = (tabTitle, nextPerms) => {
    setSelected((prev) => {
      const cleaned = (nextPerms || []).filter(Boolean);
      const exists = prev.find((x) => x.pagename === tabTitle);
      if (!cleaned.length) {
        return prev.filter((x) => x.pagename !== tabTitle);
      }
      if (exists) {
        return prev.map((x) =>
          x.pagename === tabTitle ? { ...x, permission: cleaned } : x
        );
      }
      return [...prev, { pagename: tabTitle, permission: cleaned }];
    });
  };

  const onTogglePerm = (tabTitle, permit, checked) => {
    const allowedPerms = new Set(
      (Roles?.flatMap((g) => g?.TabName) || []).filter(
        (t) => t?.title === tabTitle
      )[0]?.permission || []
    );
    if (!allowedPerms.has(permit)) return; // ignore disabled cells

    const current = getPerm(tabTitle);
    if (checked) {
      if (!current.includes(permit)) setPerm(tabTitle, [...current, permit]);
    } else {
      setPerm(
        tabTitle,
        current.filter((p) => p !== permit)
      );
    }
  };

  // group select all (allowed only)
  const onSelectAllInGroup = (group, checked) => {
    (group?.TabName || []).forEach((tab) => {
      const allowed = (tab?.permission || []).filter((p) => p !== "NA");
      const current = getPerm(tab?.title);
      if (checked) {
        const merged = Array.from(new Set([...current, ...allowed]));
        setPerm(tab?.title, merged);
      } else {
        setPerm(
          tab?.title,
          current.filter((p) => !allowed.includes(p))
        );
      }
    });
  };

  // column select all (allowed only)
  const isColumnAllChecked = (group, label) => {
    const tabs = (group?.TabName || []).filter((tab) =>
      (tab?.permission || []).includes(label)
    );
    if (!tabs.length) return false;
    return tabs.every((tab) => getPerm(tab.title).includes(label));
  };

  const onSelectColumnInGroup = (group, label, checked) => {
    (group?.TabName || []).forEach((tab) => {
      const allowed = (tab?.permission || []).includes(label);
      if (!allowed) return;
      const current = getPerm(tab.title);
      if (checked) {
        if (!current.includes(label)) setPerm(tab.title, [...current, label]);
      } else {
        if (current.includes(label)) {
          setPerm(
            tab.title,
            current.filter((p) => p !== label)
          );
        }
      }
    });
  };

  const toggleGroup = (idx) => setExpanded((p) => ({ ...p, [idx]: !p[idx] }));

  const selectedCount = useMemo(
    () =>
      Selected.reduce(
        (acc, it) =>
          acc + (Array.isArray(it.permission) ? it.permission.length : 0),
        0
      ),
    [Selected]
  );

  // submit (MASTER payload as per your original logic)
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!Role.trim()) return swal("Role name is required");
    if (!Desc.trim()) return swal("Role description is required");

    const user = JSON.parse(localStorage.getItem("userData") || "{}");
    const filteredValue = Role.trim().split(" ")[0];

    try {
      const load = {
        createdBy: user?._id,
        roleName: Role,
        id: filteredValue,
        position: 1,
        database: Database,
        desc: Desc,
        rank: 1,
        rolePermission: Selected,
      };

      await CreateRoleByMaster(load);
      swal("Created Successfully");
      navigate(-1);

      // reset
      setSelected([]);
      setDesc("");
      setRole("");
      setDatabase("");
    } catch (err) {
      console.error(err);
      swal("Failed to create role");
    }
  };

  return (
    <>
      {/* Toolbar */}
      <Row className="p-toolbar" style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card
            className="p-card"
            style={{ padding: 8, background: "#f4f7f6" }}
          >
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 10,
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  fontSize: 18,
                }}
              >
                Create Role <span className="count-pill">{selectedCount}</span>
              </div>
              <div style={{ flex: "1 1 240px" }} />
              <Button
                style={{
                  backgroundColor: "rgb(8, 91, 245)",
                  color: "white",
                  fontWeight: 600,
                  height: 32,
                }}
                onClick={() => navigate("/app/freshlist/account/RoleList")}
              >
                Back
              </Button>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Form + Permissions */}
      <Row>
        <Col xl={12}>
          <Card className="p-card" style={{ padding: 14 }}>
            <Form onSubmit={handleSubmit}>
              <Row style={{ gap: 12 }}>
                <Col md="4" sm="12">
                  <Label>Enter Role Name *</Label>
                  <Input
                    value={Role}
                    onChange={(e) => setRole(e.target.value)}
                    type="text"
                    placeholder="Enter Role Name"
                  />
                </Col>
                <Col md="6" sm="12">
                  <Label>Enter Role Description *</Label>
                  <Input
                    required
                    value={Desc}
                    onChange={(e) => setDesc(e.target.value)}
                    type="text"
                    placeholder="Enter Role Description"
                  />
                </Col>
                <Col md="4" sm="12">
                  <Label>Database</Label>
                  <Input
                    value={Database}
                    onChange={(e) => setDatabase(e.target.value)}
                    type="text"
                    placeholder="Enter database name"
                  />
                </Col>
              </Row>

              <div style={{ marginTop: 14 }}>
                {(Roles || []).map((group, gi) => {
                  const isOpen = !!expanded[gi];

                  // group level select-all state (allowed only)
                  const allPermsInGroup = (group?.TabName || []).flatMap((t) =>
                    (t?.permission || []).filter((p) => p !== "NA")
                  );
                  const tabTitles = (group?.TabName || []).map((t) => t?.title);
                  const selectedInGroup = Selected.filter((s) =>
                    tabTitles.includes(s.pagename)
                  ).flatMap((s) => s.permission);
                  const allChecked =
                    allPermsInGroup.length > 0 &&
                    allPermsInGroup.every((p) => selectedInGroup.includes(p));

                  return (
                    <div
                      key={gi}
                      className="grp-row"
                      style={{ marginBottom: 10 }}
                    >
                      <div className="grp-head" onClick={() => toggleGroup(gi)}>
                        <span className="toggle">{isOpen ? "▾" : "▸"}</span>
                        <span className="grp-title">{group?.title || "-"}</span>
                        <span className="pill">
                          {(group?.TabName || []).length} screens
                        </span>
                        <div
                          style={{
                            marginLeft: "auto",
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <Input
                            type="checkbox"
                            checked={allChecked}
                            onChange={(e) =>
                              onSelectAllInGroup(group, e.target.checked)
                            }
                            title="Select all in this group"
                            className="hdr-check"
                          />
                          <span style={{ fontSize: 12 }}>Select All</span>
                        </div>
                      </div>

                      {isOpen && (
                        <div>
                          {/* header row with ALWAYS all 6 columns */}
                          <div
                            className="perm-row"
                            style={{ background: "#fafafa", fontWeight: 700 }}
                          >
                            <div className="perm-name">Screen</div>
                            {columns.map((label) => (
                              <div key={label} className="perm-col">
                                <span>{label}</span>
                                <Input
                                  type="checkbox"
                                  className="hdr-check"
                                  checked={isColumnAllChecked(group, label)}
                                  onChange={(e) =>
                                    onSelectColumnInGroup(
                                      group,
                                      label,
                                      e.target.checked
                                    )
                                  }
                                  title={`Select all ${label}`}
                                />
                              </div>
                            ))}
                          </div>

                          {(group?.TabName || []).map((tab, ti) => {
                            const tabTitle = tab?.title;
                            const allowed = new Set(tab?.permission || []);
                            const current = getPerm(tabTitle);

                            return (
                              <div key={ti} className="perm-row">
                                <div className="perm-name">{tabTitle}</div>
                                {columns.map((label, pi) => {
                                  const isAllowed =
                                    allowed.has(label) && label !== "NA";
                                  const checked = current.includes(label);
                                  return (
                                    <div key={pi} className="perm-col">
                                      <Input
                                        type="checkbox"
                                        checked={checked}
                                        disabled={!isAllowed}
                                        onChange={(e) =>
                                          onTogglePerm(
                                            tabTitle,
                                            label,
                                            e.target.checked
                                          )
                                        }
                                        className={
                                          !isAllowed ? "disabled-box" : ""
                                        }
                                        style={{ width: 18, height: 18 }}
                                      />
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: 14,
                }}
              >
                <Button type="submit" color="primary">
                  Submit
                </Button>
              </div>
            </Form>
          </Card>
        </Col>
      </Row>
    </>
  );
}
